<template>
  <div class="g-page">
    <div class="content-container">
      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">
          <b-icon icon="trophy-fill" class="title-icon"></b-icon>
          Xodimlar Savdosi Reytingi
        </h2>
        <div class="header-actions">
          <button class="mode-btn" :class="{ active: mode === 'ranking'  }" @click="setMode('ranking')">
            <b-icon icon="bar-chart-fill"></b-icon> Reyting
          </button>
          <button class="mode-btn" :class="{ active: mode === 'compare' }" @click="setMode('compare')">
            <b-icon icon="arrow-left-right"></b-icon> Solishtirish
          </button>
        </div>
      </div>

      <!-- FILTER PANEL: RANKING -->
      <div v-if="mode === 'ranking'" class="filter-panel">
        <div class="filter-grid">
          <div class="filter-cell">
            <label>Sanadan</label>
            <input type="date" v-model="filters.date1" class="form-input" />
          </div>
          <div class="filter-cell">
            <label>Sanagacha</label>
            <input type="date" v-model="filters.date2" class="form-input" />
          </div>
          <div class="filter-cell">
            <label>Sklad</label>
            <select v-model="filters.sklad_id" class="form-input">
              <option value="">Joriy sklad</option>
              <option value="all">Barcha skladlar</option>
              <option v-for="s in sklads" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="filter-cell">
            <label>Tartiblash</label>
            <select v-model="filters.order_by" class="form-input">
              <option value="total_summa">Jami savdo (DESC)</option>
              <option value="sales_count">Savdolar soni</option>
              <option value="bonus">KPI bonus</option>
              <option value="net_summa">Sof summa</option>
              <option value="arzon">Eng ko'p arzonlatgan</option>
              <option value="avg_sale">O'rtacha savdo</option>
              <option value="seller_score">Sotuvchi obro' indeksi</option>
              <option value="premium_count">Qimmat sotgani (ko'p)</option>
              <option value="cheap_count">Arzon sotgani (ko'p)</option>
            </select>
          </div>
          <div class="filter-cell">
            <label>&nbsp;</label>
            <div class="action-row">
              <button class="btn-primary" @click="loadRanking()" :disabled="loading">
                <b-icon icon="search"></b-icon> {{ loading ? 'Yuklanmoqda...' : 'Hisoblash' }}
              </button>
              <button class="btn-secondary" @click="quickRange('today')" title="Bugun">Bugun</button>
              <button class="btn-secondary" @click="quickRange('week')"  title="Hafta">Hafta</button>
              <button class="btn-secondary" @click="quickRange('month')" title="Oy">Oy</button>
            </div>
          </div>
        </div>
      </div>

      <!-- FILTER PANEL: COMPARE -->
      <div v-if="mode === 'compare'" class="filter-panel">
        <div class="compare-grid">
          <div class="compare-block">
            <h4 class="compare-title">A — 1-davr</h4>
            <div class="compare-inputs">
              <input type="date" v-model="compare.a_date1" class="form-input" placeholder="dan" />
              <input type="date" v-model="compare.a_date2" class="form-input" placeholder="gacha" />
            </div>
          </div>
          <div class="compare-vs">VS</div>
          <div class="compare-block">
            <h4 class="compare-title">B — 2-davr</h4>
            <div class="compare-inputs">
              <input type="date" v-model="compare.b_date1" class="form-input" placeholder="dan" />
              <input type="date" v-model="compare.b_date2" class="form-input" placeholder="gacha" />
            </div>
          </div>
          <div class="compare-action">
            <button class="btn-primary" @click="loadCompare()" :disabled="loading">
              <b-icon icon="bar-chart-line-fill"></b-icon> Solishtirish
            </button>
          </div>
        </div>
      </div>

      <!-- SUMMARY CARDS — RANKING MODE -->
      <div v-if="mode === 'ranking' && totals" class="summary-row">
        <div class="summary-card primary">
          <div class="card-label">Jami savdo</div>
          <div class="card-value">{{ formatMoney(totals.total_summa) }}</div>
          <div class="card-sub">{{ totals.sales_count }} ta</div>
        </div>
        <div class="summary-card success">
          <div class="card-label">To'liq narxda</div>
          <div class="card-value">{{ totals.full_count || 0 }}</div>
          <div class="card-sub">chegirmasiz savdo</div>
        </div>
        <div class="summary-card purple">
          <div class="card-label">Qimmat sotgan</div>
          <div class="card-value">{{ totals.premium_count || 0 }}</div>
          <div class="card-sub">+{{ formatMoney(totals.premium_extra || 0) }} ustama</div>
        </div>
        <div class="summary-card warning">
          <div class="card-label">Arzonlatilgan</div>
          <div class="card-value">{{ formatMoney(totals.arzon_summa) }}</div>
          <div class="card-sub">{{ totals.cheap_count || 0 }} ta arzon savdo</div>
        </div>
        <div class="summary-card info">
          <div class="card-label">KPI bonuslar</div>
          <div class="card-value">{{ formatMoney(totals.kpi_bonus) }}</div>
          <div class="card-sub">jami xodimlar</div>
        </div>
        <div class="summary-card danger">
          <div class="card-label">Qarzga sotilgan</div>
          <div class="card-value">{{ formatMoney(totals.qarz_summa) }}</div>
          <div class="card-sub">to'lanmagan</div>
        </div>
      </div>

      <!-- TABLE: RANKING -->
      <div v-if="mode === 'ranking'" class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th style="width:60px">#</th>
                <th>Xodim</th>
                <th class="num">Sotuvlar</th>
                <th class="num">Jami summa</th>
                <th style="text-align:center; min-width:220px">Sotuv toifasi</th>
                <th class="num">Arzonlatilgan</th>
                <th class="num">O'rtacha %</th>
                <th class="num">Obro'</th>
                <th class="num">Qarz</th>
                <th class="num">KPI %</th>
                <th class="num">KPI bonus</th>
                <th style="width:90px;text-align:center">Tarixi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in items" :key="r.user_id" :class="rowClass(r.rank)">
                <td>
                  <span class="rank-badge" :class="rankBadgeClass(r.rank)">
                    <b-icon v-if="r.rank === 1" icon="trophy-fill"></b-icon>
                    {{ r.rank }}
                  </span>
                </td>
                <td class="name-cell">
                  <div class="user-name">{{ r.first_name }} {{ r.last_name }}</div>
                  <div class="user-username">@{{ r.username }}</div>
                </td>
                <td class="num">
                  <span class="count-badge">{{ r.sales_count }}</span>
                </td>
                <td class="num bold money">{{ formatMoney(r.total_summa) }}</td>

                <!-- TOIFA bar: Qimmat | To'liq | Mayda | Chegirma | Arzon -->
                <td>
                  <div class="cat-bar">
                    <span v-if="r.premium_count > 0" class="cat-pill cat-premium" :title="r.premium_count + ' ta qimmat'">
                      🟣 {{ r.premium_count }}
                    </span>
                    <span v-if="r.full_count > 0" class="cat-pill cat-full" :title="r.full_count + ' ta to\'liq narx'">
                      🟢 {{ r.full_count }}
                    </span>
                    <span v-if="r.small_count > 0" class="cat-pill cat-small" :title="r.small_count + ' ta mayda chegirma'">
                      ⚪ {{ r.small_count }}
                    </span>
                    <span v-if="r.medium_count > 0" class="cat-pill cat-discount" :title="r.medium_count + ' ta chegirmali'">
                      🟡 {{ r.medium_count }}
                    </span>
                    <span v-if="r.cheap_count > 0" class="cat-pill cat-cheap" :title="r.cheap_count + ' ta arzon'">
                      🔴 {{ r.cheap_count }}
                    </span>
                  </div>
                </td>

                <td class="num">
                  <span class="discount-badge" v-if="r.arzon_summa > 0">
                    -{{ formatMoney(r.arzon_summa) }}
                  </span>
                  <span v-else class="muted">—</span>
                </td>
                <td class="num">
                  <span :class="avgPctClass(r.avg_discount_pct)">
                    {{ r.avg_discount_pct }}%
                  </span>
                </td>
                <td class="num">
                  <span class="score-badge" :class="scoreClass(r.seller_score)">
                    {{ r.seller_score }}
                  </span>
                </td>
                <td class="num">
                  <span v-if="r.qarz_summa > 0" class="debt-text">{{ formatMoney(r.qarz_summa) }}</span>
                  <span v-else class="muted">—</span>
                </td>
                <td class="num">
                  <span class="kpi-pct">{{ r.kpi_percent }}%</span>
                </td>
                <td class="num bonus-cell">{{ formatMoney(r.kpi_bonus) }}</td>
                <td style="text-align:center">
                  <button class="icon-btn" @click="openHistory(r)" title="Savdo tarixi">
                    <b-icon icon="clock-history"></b-icon>
                  </button>
                </td>
              </tr>
              <tr v-if="items.length === 0 && !loading">
                <td colspan="12" class="empty-row">Ma'lumot topilmadi. Sanani tanlab "Hisoblash" tugmasini bosing.</td>
              </tr>
              <tr v-if="loading">
                <td colspan="12" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TABLE: COMPARE -->
      <div v-if="mode === 'compare'" class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th style="width:60px">#</th>
                <th>Xodim</th>
                <th class="num">A: summa</th>
                <th class="num">A: ta</th>
                <th class="num">B: summa</th>
                <th class="num">B: ta</th>
                <th class="num">Farq</th>
                <th class="num">%</th>
                <th>Trend</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in compareItems" :key="r.user_id">
                <td><span class="rank-badge">{{ r.rank }}</span></td>
                <td class="name-cell">
                  <div class="user-name">{{ r.first_name }} {{ r.last_name }}</div>
                  <div class="user-username">@{{ r.username }}</div>
                </td>
                <td class="num muted">{{ formatMoney(r.a_total) }}</td>
                <td class="num">{{ r.a_count }}</td>
                <td class="num bold money">{{ formatMoney(r.b_total) }}</td>
                <td class="num">{{ r.b_count }}</td>
                <td class="num" :class="diffClass(r.diff_summa)">
                  {{ r.diff_summa > 0 ? '+' : '' }}{{ formatMoney(r.diff_summa) }}
                </td>
                <td class="num" :class="diffClass(r.diff_summa)">
                  {{ r.diff_pct > 0 ? '+' : '' }}{{ r.diff_pct }}%
                </td>
                <td>
                  <span class="trend-badge" :class="'trend-' + r.trend">
                    <b-icon :icon="trendIcon(r.trend)"></b-icon>
                    {{ trendLabel(r.trend) }}
                  </span>
                </td>
              </tr>
              <tr v-if="compareItems.length === 0 && !loading">
                <td colspan="9" class="empty-row">Ikkala davrni tanlab "Solishtirish" tugmasini bosing</td>
              </tr>
              <tr v-if="loading">
                <td colspan="9" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- HISTORY MODAL -->
    <b-modal id="modal-history" hide-footer size="xl" no-close-on-backdrop modal-class="modern-modal">
      <template #modal-title>
        <span v-if="historyUser">
          <b-icon icon="clock-history"></b-icon>
          {{ historyUser.first_name }} {{ historyUser.last_name }} — savdo tarixi
        </span>
      </template>

      <div class="history-content">
        <!-- Summary -->
        <div v-if="history.summary" class="hist-summary">
          <div class="hist-card">
            <span class="hist-label">Jami</span>
            <span class="hist-val">{{ formatMoney(history.summary.total_summa) }}</span>
          </div>
          <div class="hist-card">
            <span class="hist-label">Asl narx</span>
            <span class="hist-val">{{ formatMoney(history.summary.original_summa) }}</span>
          </div>
          <div class="hist-card warn">
            <span class="hist-label">Arzonlatilgan</span>
            <span class="hist-val">{{ formatMoney(history.summary.arzon_summa) }}</span>
          </div>
          <div class="hist-card info">
            <span class="hist-label">KPI bonus ({{ history.summary.kpi_percent }}%)</span>
            <span class="hist-val">{{ formatMoney(history.summary.kpi_bonus) }}</span>
          </div>
          <div class="hist-card danger">
            <span class="hist-label">Qarz</span>
            <span class="hist-val">{{ formatMoney(history.summary.qarz_summa) }}</span>
          </div>
        </div>

        <!-- Filters -->
        <div class="hist-filters">
          <input type="date" v-model="historyFilter.date1" class="form-input small" />
          <input type="date" v-model="historyFilter.date2" class="form-input small" />
          <button class="btn-secondary" @click="loadHistory()">Yangilash</button>
        </div>

        <!-- Table -->
        <div class="hist-table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Sana</th>
                <th>Mijoz</th>
                <th>Avtomobil</th>
                <th>To'lov</th>
                <th class="num">Asl narx</th>
                <th class="num">Sotilgan</th>
                <th class="num">Arzon (%)</th>
                <th class="num">Qarz</th>
                <th>Kategoriya</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(h, i) in history.items" :key="h.id">
                <td>{{ i + 1 }}</td>
                <td class="muted">{{ formatDate(h.created_at) }}</td>
                <td>{{ h.kontragent_name || '—' }}</td>
                <td>{{ h.car_number || '—' }}</td>
                <td>
                  <!-- To'lov breakdown — pay_type jadvalidan dinamik -->
                  <div class="pay-pills-mini">
                    <!-- Yangi: dinamik payments[] -->
                    <template v-if="Array.isArray(h.payments) && h.payments.length > 0">
                      <span v-for="p in h.payments" :key="p.id"
                        class="pp-mini"
                        :class="payPillClass(p.pay_type_name)"
                        :title="p.pay_type_name + ': ' + (isPayDollar(p.pay_type_name) ? p.summa + '$' : formatMoney(p.summa))">
                        {{ payPillIcon(p.pay_type_name) }}
                        {{ isPayDollar(p.pay_type_name) ? Number(p.summa) + '$' : shortNum(p.summa) }}
                      </span>
                    </template>
                    <!-- Eski savdolar fallback -->
                    <template v-else>
                      <span v-if="Number(h.cash_summa || 0) > 0" class="pp-mini pp-naqd"
                        :title="'Naqd: ' + formatMoney(h.cash_summa)">💵 {{ shortNum(h.cash_summa) }}</span>
                      <span v-if="Number(h.plastic_summa || 0) > 0" class="pp-mini pp-plastic"
                        :title="'Plastik: ' + formatMoney(h.plastic_summa)">💳 {{ shortNum(h.plastic_summa) }}</span>
                      <span v-if="Number(h.shot_summa || 0) > 0" class="pp-mini pp-shot"
                        :title="'Shot: ' + formatMoney(h.shot_summa)">📲 {{ shortNum(h.shot_summa) }}</span>
                      <span v-if="Number(h.dollar_summa || 0) > 0" class="pp-mini pp-dollar"
                        :title="'Dollar: ' + h.dollar_summa + '$'">💲 {{ Number(h.dollar_summa) }}$</span>
                      <span v-if="!Number(h.cash_summa) && !Number(h.plastic_summa) && !Number(h.shot_summa) && !Number(h.dollar_summa)"
                            class="pay-tag">{{ h.pay_type_name }}</span>
                    </template>
                  </div>
                </td>
                <td class="num muted">{{ formatMoney(h.original) }}</td>
                <td class="num bold money">{{ formatMoney(h.summa) }}</td>
                <td class="num">
                  <span v-if="h.arzon > 0" class="discount-badge">-{{ h.arzon_pct }}%</span>
                  <span v-else class="muted">—</span>
                </td>
                <td class="num">
                  <div v-if="h.qarz_summa > 0 || Number(h.qarz_dollar_summa || 0) > 0">
                    <span v-if="h.qarz_summa > 0" class="debt-text">{{ formatMoney(h.qarz_summa) }}</span>
                    <span v-if="Number(h.qarz_dollar_summa || 0) > 0" class="debt-text" style="display:block">
                      + {{ Number(h.qarz_dollar_summa) }}$
                    </span>
                  </div>
                  <span v-else class="muted">—</span>
                </td>
                <td>
                  <span class="cat-badge" :class="'cat-' + h.category">
                    {{ catLabel(h.category) }}
                  </span>
                </td>
              </tr>
              <tr v-if="history.items.length === 0">
                <td colspan="10" class="empty-row">Bu davrda savdolar yo'q</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'StaffSalesRanking',
  data() {
    return {
      mode: 'ranking',
      loading: false,

      sklads: [],

      filters: {
        date1: this.firstDayOfMonth(),
        date2: this.today(),
        sklad_id: '',
        order_by: 'total_summa',
      },

      compare: {
        a_date1: '', a_date2: '',
        b_date1: '', b_date2: '',
      },

      items: [],
      compareItems: [],
      totals: null,

      // History modal
      historyUser: null,
      historyFilter: { date1: '', date2: '' },
      history: { items: [], summary: null, total: 0 },
    };
  },
  methods: {
    /* ─── Helpers ─── */
    today() {
      const d = new Date();
      return d.toISOString().slice(0, 10);
    },
    firstDayOfMonth() {
      const d = new Date();
      d.setDate(1);
      return d.toISOString().slice(0, 10);
    },
    toUnix(dateStr, endOfDay = false) {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      if (endOfDay) d.setHours(23, 59, 59, 999);
      else d.setHours(0, 0, 0, 0);
      return Math.floor(d.getTime() / 1000);
    },
    formatMoney(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' so\'m';
    },
    shortNum(n) {
      const v = parseFloat(n || 0);
      if (!v) return '0';
      if (v >= 1000000) return (v / 1000000).toFixed(v % 1000000 === 0 ? 0 : 1) + 'M';
      if (v >= 1000)    return (v / 1000).toFixed(v % 1000 === 0 ? 0 : 1) + 'K';
      return String(v);
    },
    isPayDollar(name) {
      const n = String(name || '').toLowerCase();
      return n.includes('dollar') || n.includes('usd') || n.includes('$') || n.startsWith('доллар');
    },
    payPillIcon(name) {
      const n = String(name || '').toLowerCase();
      if (this.isPayDollar(name)) return '💲';
      if (n.includes('plast') || n.includes('пласт') || n.includes('card') || n.includes('uzcard') || n.includes('humo')) return '💳';
      if (n.includes('shot') || n.includes('шот') || n.includes('перевод')) return '📲';
      if (n.includes('naqd') || n.includes('нақд') || n.includes('cash')) return '💵';
      return '💰';
    },
    payPillClass(name) {
      const n = String(name || '').toLowerCase();
      if (this.isPayDollar(name)) return 'pp-dollar';
      if (n.includes('plast') || n.includes('uzcard') || n.includes('humo') || n.includes('card')) return 'pp-plastic';
      if (n.includes('shot') || n.includes('перевод')) return 'pp-shot';
      if (n.includes('naqd') || n.includes('cash')) return 'pp-naqd';
      return 'pp-other';
    },
    formatDate(unix) {
      if (!unix) return '—';
      const d = new Date(unix * 1000);
      return d.toLocaleDateString('uz-UZ') + ' ' + d.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' });
    },
    rowClass(rank) {
      return rank <= 3 ? 'top-row' : '';
    },
    rankBadgeClass(rank) {
      if (rank === 1) return 'gold';
      if (rank === 2) return 'silver';
      if (rank === 3) return 'bronze';
      return 'normal';
    },
    avgPctClass(pct) {
      const v = parseFloat(pct || 0);
      if (v === 0)   return 'pct-good';
      if (v < 5)     return 'pct-ok';
      if (v < 15)    return 'pct-warn';
      return 'pct-bad';
    },
    scoreClass(score) {
      const s = parseInt(score || 0);
      if (s >= 80) return 'score-high';
      if (s >= 50) return 'score-mid';
      return 'score-low';
    },
    diffClass(diff) {
      if (diff > 0) return 'positive';
      if (diff < 0) return 'negative';
      return 'muted';
    },
    trendIcon(t) {
      return { up: 'arrow-up-right', down: 'arrow-down-right', flat: 'dash' }[t] || 'dash';
    },
    trendLabel(t) {
      return { up: 'Yaxshi', down: 'Pasaygan', flat: 'O\'zgarmagan' }[t] || '';
    },
    catLabel(c) {
      return {
        premium:  'Qimmat',
        full:     "To'liq narx",
        small:    'Mayda',
        discount: 'Chegirmali',
        cheap:    'Arzon',
        unknown:  '—',
      }[c] || c;
    },

    /* ─── Mode ─── */
    setMode(m) {
      this.mode = m;
      this.items = [];
      this.compareItems = [];
      this.totals = null;
    },

    quickRange(type) {
      const now = new Date();
      const t = (d) => d.toISOString().slice(0, 10);
      if (type === 'today') {
        this.filters.date1 = t(now);
        this.filters.date2 = t(now);
      } else if (type === 'week') {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        this.filters.date1 = t(d);
        this.filters.date2 = t(now);
      } else if (type === 'month') {
        this.filters.date1 = this.firstDayOfMonth();
        this.filters.date2 = t(now);
      }
      this.loadRanking();
    },

    /* ─── API ─── */
    async loadRanking() {
      this.loading = true;
      try {
        const params = {
          date1: this.toUnix(this.filters.date1),
          date2: this.toUnix(this.filters.date2, true),
          order_by: this.filters.order_by,
          limit: 200,
        };
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;

        const res = await axios.get('api/v1/report/staff-sales-ranking', { params });
        this.items = res.data.items || [];
        this.totals = res.data.totals || null;
      } finally {
        this.loading = false;
      }
    },

    async loadCompare() {
      if (!this.compare.a_date1 || !this.compare.a_date2 || !this.compare.b_date1 || !this.compare.b_date2) {
        alert('Ikkala davrni to\'liq tanlang');
        return;
      }
      this.loading = true;
      try {
        const params = {
          a_date1: this.toUnix(this.compare.a_date1),
          a_date2: this.toUnix(this.compare.a_date2, true),
          b_date1: this.toUnix(this.compare.b_date1),
          b_date2: this.toUnix(this.compare.b_date2, true),
        };
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;

        const res = await axios.get('api/v1/report/staff-sales-compare', { params });
        this.compareItems = res.data.items || [];
      } finally {
        this.loading = false;
      }
    },

    openHistory(user) {
      this.historyUser = user;
      this.historyFilter.date1 = this.filters.date1;
      this.historyFilter.date2 = this.filters.date2;
      this.history = { items: [], summary: null, total: 0 };
      this.$bvModal.show('modal-history');
      this.loadHistory();
    },

    async loadHistory() {
      if (!this.historyUser) return;
      const params = {
        date1: this.toUnix(this.historyFilter.date1),
        date2: this.toUnix(this.historyFilter.date2, true),
        limit: 200,
      };
      if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;

      const res = await axios.get(`api/v1/report/staff-sales-history/${this.historyUser.user_id}`, { params });
      this.history = res.data;
    },

    loadSklads() {
      axios.get('api/v1/sklads').then(r => { this.sklads = r.data || []; }).catch(() => {});
    },
  },
  mounted() {
    this.loadSklads();
    this.loadRanking();
  },
};
</script>

<style scoped>
.g-page { padding: 20px; height: 100%; overflow-y: auto; background: #f8fafc; }
.content-container { max-width: 1400px; margin: 0 auto; }

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
}

.title-icon { color: #f59e0b; font-size: 24px; }

.header-actions { display: flex; gap: 6px; }

.mode-btn {
  background: white;
  border: 2px solid #e2e8f0;
  padding: 8px 18px;
  border-radius: 10px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: 0.2s;
}

.mode-btn.active {
  border-color: #2563eb;
  color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 2px 8px rgba(37,99,235,0.15);
}

/* FILTER */
.filter-panel {
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  padding: 16px;
  margin-bottom: 16px;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.filter-cell label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input {
  width: 100%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 14px;
  color: #1e293b;
  outline: none;
  transition: 0.2s;
}

.form-input:focus {
  border-color: #2563eb;
  background: white;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
}

.form-input.small { padding: 7px 10px; max-width: 160px; }

.action-row { display: flex; gap: 6px; }

.btn-primary {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 9px 18px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(37,99,235,0.25);
  transition: 0.2s;
}

.btn-primary:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }
.btn-primary:not(:disabled):hover { transform: translateY(-1px); }

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
  border: none;
  padding: 9px 14px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: 0.2s;
}

.btn-secondary:hover { background: #e2e8f0; }

/* COMPARE */
.compare-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto;
  gap: 14px;
  align-items: end;
}

.compare-block { background: #f8fafc; padding: 12px; border-radius: 10px; }
.compare-title { font-size: 13px; color: #64748b; margin: 0 0 8px 0; font-weight: 700; }
.compare-inputs { display: flex; gap: 8px; }

.compare-vs {
  font-weight: 800;
  color: #2563eb;
  font-size: 18px;
  padding: 10px;
  align-self: center;
}

/* SUMMARY CARDS */
.summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card {
  background: white;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border-left: 4px solid #94a3b8;
}

.summary-card.primary { border-color: #2563eb; }
.summary-card.success { border-color: #22c55e; }
.summary-card.warning { border-color: #f59e0b; }
.summary-card.info    { border-color: #06b6d4; }
.summary-card.danger  { border-color: #ef4444; }
.summary-card.purple  { border-color: #7c3aed; }

.card-label {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-value {
  font-size: 22px;
  font-weight: 800;
  color: #1e293b;
  margin: 4px 0 2px 0;
}

.card-sub { font-size: 12px; color: #64748b; }

/* TABLE */
.table-card {
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 380px);
  overflow-y: auto;
}

.custom-table { width: 100%; border-collapse: collapse; }

.custom-table thead { position: sticky; top: 0; z-index: 2; }

.custom-table th {
  background: #f8fafc;
  padding: 12px 14px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
  text-align: left;
}

.custom-table th.num,
.custom-table td.num { text-align: right; }

.custom-table td {
  padding: 11px 14px;
  border-bottom: 1px solid #f8fafc;
  font-size: 14px;
  color: #334155;
  vertical-align: middle;
}

.top-row { background: linear-gradient(90deg, rgba(245,158,11,0.04), transparent); }

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 32px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 13px;
  background: #f1f5f9;
  color: #64748b;
}

.rank-badge.gold   { background: linear-gradient(135deg, #fde047, #f59e0b); color: white; box-shadow: 0 2px 6px rgba(245,158,11,0.3); }
.rank-badge.silver { background: linear-gradient(135deg, #e2e8f0, #94a3b8); color: white; }
.rank-badge.bronze { background: linear-gradient(135deg, #fdba74, #c2410c); color: white; }

.name-cell .user-name { font-weight: 700; color: #1e293b; }
.name-cell .user-username { font-size: 11px; color: #94a3b8; font-weight: 500; margin-top: 2px; }

.bold { font-weight: 800; color: #1e293b; }
.money { font-family: 'Inter', system-ui, sans-serif; }
.muted { color: #94a3b8; }

.count-badge {
  background: #eff6ff;
  color: #1d4ed8;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
}

.discount-badge {
  background: #fef3c7;
  color: #b45309;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
}

.debt-text { color: #ef4444; font-weight: 700; }

.kpi-pct {
  background: #f0fdf4;
  color: #15803d;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
}

.bonus-cell { color: #15803d; font-weight: 700; }

/* TOIFA bar — savdo turi pillalari */
.cat-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
}

.cat-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  cursor: help;
  white-space: nowrap;
}

.cat-premium  { background: #ede9fe; color: #6d28d9; }
.cat-full     { background: #dcfce7; color: #15803d; }
.cat-small    { background: #f1f5f9; color: #475569; }
.cat-discount { background: #fef3c7; color: #b45309; }
.cat-cheap    { background: #fee2e2; color: #b91c1c; }

/* O'rtacha % rang kodi */
.pct-good { color: #15803d; font-weight: 700; }
.pct-ok   { color: #475569; font-weight: 700; }
.pct-warn { color: #b45309; font-weight: 700; }
.pct-bad  { color: #b91c1c; font-weight: 800; }

/* Sotuvchi obro' indeksi */
.score-badge {
  display: inline-block;
  min-width: 38px;
  padding: 4px 10px;
  border-radius: 8px;
  font-weight: 800;
  font-size: 13px;
}

.score-high { background: #dcfce7; color: #15803d; }
.score-mid  { background: #fef3c7; color: #b45309; }
.score-low  { background: #fee2e2; color: #b91c1c; }

.icon-btn {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #2563eb;
  transition: 0.2s;
}

.icon-btn:hover { background: #2563eb; color: white; }

.empty-row {
  text-align: center;
  padding: 50px !important;
  color: #94a3b8;
  font-style: italic;
}

.loading-row { text-align: center; padding: 30px; color: #2563eb; }

/* SPINNER */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #dbeafe;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 6px;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* COMPARE TABLE */
.positive { color: #15803d; font-weight: 700; }
.negative { color: #ef4444; font-weight: 700; }

.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
}

.trend-up   { background: #dcfce7; color: #15803d; }
.trend-down { background: #fee2e2; color: #b91c1c; }
.trend-flat { background: #f1f5f9; color: #64748b; }

/* HISTORY MODAL */
.history-content { padding: 5px; }

.hist-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.hist-card {
  background: #f8fafc;
  padding: 12px 14px;
  border-radius: 10px;
  border-left: 3px solid #2563eb;
}

.hist-card.warn   { border-color: #f59e0b; }
.hist-card.info   { border-color: #06b6d4; }
.hist-card.danger { border-color: #ef4444; }

.hist-label { font-size: 11px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.hist-val   { display: block; font-size: 16px; font-weight: 800; color: #1e293b; margin-top: 2px; }

.hist-filters {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  align-items: center;
}

.hist-table-wrapper {
  max-height: 50vh;
  overflow-y: auto;
  border-radius: 10px;
  border: 1px solid #f1f5f9;
}

.pay-tag {
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
}

/* Pay-pills mini (history modal) */
.pay-pills-mini {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.pp-mini {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  font-family: 'Inter', system-ui, sans-serif;
  white-space: nowrap;
  cursor: help;
}

.pp-mini.pp-naqd    { background: #dcfce7; color: #15803d; }
.pp-mini.pp-plastic { background: #dbeafe; color: #1d4ed8; }
.pp-mini.pp-shot    { background: #fef3c7; color: #b45309; }
.pp-mini.pp-dollar  { background: #f0fdf4; color: #15803d; border: 1px solid #16a34a; }
.pp-mini.pp-other   { background: #f1f5f9; color: #475569; }

.cat-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
}

.cat-cheap    { background: #fee2e2; color: #b91c1c; }
.cat-discount { background: #fef3c7; color: #b45309; }
.cat-normal   { background: #f1f5f9; color: #64748b; }
.cat-small    { background: #f1f5f9; color: #475569; }
.cat-full     { background: #dcfce7; color: #15803d; }
.cat-premium  { background: #ede9fe; color: #6d28d9; }
.cat-unknown  { background: #f8fafc; color: #94a3b8; }
</style>
